import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { ScrollArea } from '../components/ui/scroll-area';
import { Separator } from '../components/ui/separator';
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from '../components/ui/tooltip';
import {
  Bot, Plus, Trash2, Edit2, Save, Search, Code, Shield, Pen, Zap,
  BookOpen, Workflow, Copy, ChevronRight, Puzzle, Link, Settings2,
  Palette, MoveUp, MoveDown, Globe, Plug, Server, Wrench, Play,
  ShieldAlert, Fingerprint, Terminal, CheckCircle2, XCircle, Loader2
} from 'lucide-react';
import {
  getAgentConfigs, createAgentConfig, updateAgentConfig, deleteAgentConfig,
  getSkills, createSkill, updateSkill, deleteSkill,
  getWorkflows, createWorkflow, updateWorkflow, deleteWorkflow,
  seedDefaults, getLLMProviders, updateAgentMCP,
  getTools, executeTool
} from '../lib/api';

const DEFAULT_STEP_OPTIONS = [
  { value: 'research', label: 'Research Agent' },
  { value: 'content', label: 'Content Writer' },
  { value: 'html', label: 'HTML Builder' },
  { value: 'review', label: 'CXO Reviewer' },
];

const ICON_MAP = {
  'bot': Bot, 'search': Search, 'code': Code, 'shield-check': Shield,
  'pen-tool': Pen, 'zap': Zap, 'book-open': BookOpen, 'puzzle': Puzzle,
};

const TOOL_ICON_MAP = {
  'globe': Globe, 'shield-alert': ShieldAlert, 'server': Server,
  'search': Search, 'fingerprint': Fingerprint, 'wrench': Wrench,
  'terminal': Terminal, 'code': Code,
};

export default function AgentStudio() {
  const [agents, setAgents] = useState([]);
  const [skills, setSkills] = useState([]);
  const [workflows, setWorkflows] = useState([]);
  const [tools, setTools] = useState([]);
  const [activeTab, setActiveTab] = useState('agents');
  const [loading, setLoading] = useState(true);

  // Agent editor state
  const [editingAgent, setEditingAgent] = useState(null);
  const [agentForm, setAgentForm] = useState({
    name: '', description: '', system_prompt: '', model_provider: 'anthropic',
    model_name: 'claude-4-sonnet-20250514', skills: [], workflows: [],
    output_schema: '', temperature: 0.7, max_tokens: 4096, tags: [],
    icon: 'bot', color: '#0891b2', fallback_model_provider: '', fallback_model_name: '',
    mcp_servers: []
  });

  // Skill editor state
  const [editingSkill, setEditingSkill] = useState(null);
  const [skillForm, setSkillForm] = useState({ name: '', description: '', content: '', tags: [], tools: [] });

  // Workflow editor state
  const [editingWorkflow, setEditingWorkflow] = useState(null);
  const [workflowForm, setWorkflowForm] = useState({ name: '', description: '', steps: [], tags: [] });

  // Tool testing state
  const [testingTool, setTestingTool] = useState(null);
  const [toolTestParams, setToolTestParams] = useState({});
  const [toolTestResult, setToolTestResult] = useState(null);
  const [toolTestLoading, setToolTestLoading] = useState(false);

  const [showAgentDialog, setShowAgentDialog] = useState(false);
  const [showSkillDialog, setShowSkillDialog] = useState(false);
  const [showWorkflowDialog, setShowWorkflowDialog] = useState(false);
  const [showToolTestDialog, setShowToolTestDialog] = useState(false);
  const [llmProviders, setLlmProviders] = useState([]);

  // Compute which workflows use each agent (by agent name or agent key)
  const getAgentWorkflowUsage = React.useCallback((agent) => {
    const agentName = (agent.name || '').toLowerCase();
    const agentKey = (agent.step_key || agent.name || '').toLowerCase().replace(/\s+/g, '_');
    const matchingWorkflows = [];
    
    workflows.forEach(w => {
      const steps = w.steps || [];
      const match = steps.find(s => {
        const stepKey = (s.agent_key || '').toLowerCase();
        const stepAgentId = (s.agent_id || '').toLowerCase();
        const stepName = (s.name || '').toLowerCase();
        return stepKey === agentKey || stepAgentId === agent.id || 
               stepName.includes(agentName) || agentName.includes(stepName);
      });
      if (match) {
        matchingWorkflows.push({
          name: w.name,
          stepName: match.name || match.agent_key || 'Unknown step'
        });
      }
    });
    
    return matchingWorkflows;
  }, [workflows]);

  useEffect(() => { loadAll(); }, []);

  // Dynamically compute step options from all workflows + defaults
  const STEP_OPTIONS = React.useMemo(() => {
    const seen = new Set(DEFAULT_STEP_OPTIONS.map(o => o.value));
    const options = [...DEFAULT_STEP_OPTIONS];
    
    // Extract unique agent_keys from all workflow steps
    workflows.forEach(w => {
      (w.steps || []).forEach(s => {
        const key = s.agent_key || s.agent_id;
        if (key && !seen.has(key)) {
          seen.add(key);
          options.push({
            value: key,
            label: (s.name || key).replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
          });
        }
      });
    });
    
    return options;
  }, [workflows]);

  const loadAll = async () => {
    try {
      const [agentsRes, skillsRes, workflowsRes, providersRes, toolsRes] = await Promise.all([
        getAgentConfigs(), getSkills(), getWorkflows(), getLLMProviders(), getTools()
      ]);
      setAgents(agentsRes.data);
      setSkills(skillsRes.data);
      setWorkflows(workflowsRes.data);
      setLlmProviders(providersRes.data?.providers || []);
      setTools(toolsRes.data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSeedDefaults = async () => {
    try {
      const res = await seedDefaults();
      toast.success(res.data.message);
      loadAll();
    } catch (err) {
      toast.error('Failed to seed defaults');
    }
  };

  // ---- AGENTS ----
  const resetAgentForm = () => {
    setAgentForm({
      name: '', description: '', system_prompt: '', model_provider: 'anthropic',
      model_name: 'claude-4-sonnet-20250514', skills: [], workflows: [],
      output_schema: '', temperature: 0.7, max_tokens: 4096, tags: [],
      icon: 'bot', color: '#0891b2', fallback_model_provider: '', fallback_model_name: '',
      mcp_servers: []
    });
    setEditingAgent(null);
  };

  const openAgentEditor = (agent = null) => {
    if (agent) {
      setEditingAgent(agent);
      setAgentForm({
        name: agent.name || '', description: agent.description || '',
        system_prompt: agent.system_prompt || '', model_provider: agent.model_provider || 'anthropic',
        model_name: agent.model_name || 'claude-4-sonnet-20250514',
        skills: agent.skills || [], workflows: agent.workflows || [],
        output_schema: agent.output_schema || '', temperature: agent.temperature || 0.7,
        max_tokens: agent.max_tokens || 4096, tags: agent.tags || [],
        icon: agent.icon || 'bot', color: agent.color || '#0891b2',
        fallback_model_provider: agent.fallback_model_provider || '',
        fallback_model_name: agent.fallback_model_name || '',
        mcp_servers: agent.mcp_servers || []
      });
    } else {
      resetAgentForm();
    }
    setShowAgentDialog(true);
  };

  const handleSaveAgent = async () => {
    if (!agentForm.name.trim()) { toast.error('Agent name required'); return; }
    if (!agentForm.system_prompt.trim()) { toast.error('System prompt required'); return; }
    try {
      if (editingAgent) {
        await updateAgentConfig(editingAgent.id, agentForm);
        toast.success('Agent updated');
      } else {
        await createAgentConfig(agentForm);
        toast.success('Agent created');
      }
      setShowAgentDialog(false);
      resetAgentForm();
      loadAll();
    } catch (err) {
      toast.error('Failed to save agent');
    }
  };

  const handleDeleteAgent = async (id) => {
    if (!window.confirm('Delete this agent?')) return;
    try {
      await deleteAgentConfig(id);
      setAgents(agents.filter(a => a.id !== id));
      toast.success('Agent deleted');
    } catch (err) { toast.error('Failed to delete'); }
  };

  // ---- SKILLS ----
  const resetSkillForm = () => {
    setSkillForm({ name: '', description: '', content: '', tags: [], tools: [] });
    setEditingSkill(null);
  };

  const openSkillEditor = (skill = null) => {
    if (skill) {
      setEditingSkill(skill);
      setSkillForm({
        name: skill.name, description: skill.description || '',
        content: skill.content || '', tags: skill.tags || [],
        tools: skill.tools || []
      });
    } else { resetSkillForm(); }
    setShowSkillDialog(true);
  };

  const handleSaveSkill = async () => {
    if (!skillForm.name.trim()) { toast.error('Skill name required'); return; }
    try {
      if (editingSkill) {
        await updateSkill(editingSkill.id, skillForm);
        toast.success('Skill updated');
      } else {
        await createSkill(skillForm);
        toast.success('Skill created');
      }
      setShowSkillDialog(false);
      resetSkillForm();
      loadAll();
    } catch (err) { toast.error('Failed to save skill'); }
  };

  const handleDeleteSkill = async (id) => {
    if (!window.confirm('Delete this skill?')) return;
    try {
      await deleteSkill(id);
      setSkills(skills.filter(s => s.id !== id));
      toast.success('Skill deleted');
    } catch (err) { toast.error('Failed to delete'); }
  };

  // ---- WORKFLOWS ----
  const resetWorkflowForm = () => {
    setWorkflowForm({ name: '', description: '', steps: [], tags: [] });
    setEditingWorkflow(null);
  };

  const openWorkflowEditor = (wf = null) => {
    if (wf) {
      setEditingWorkflow(wf);
      setWorkflowForm({ name: wf.name, description: wf.description || '', steps: wf.steps || [], tags: wf.tags || [] });
    } else { resetWorkflowForm(); }
    setShowWorkflowDialog(true);
  };

  const addWorkflowStep = () => {
    setWorkflowForm(prev => ({
      ...prev,
      steps: [...prev.steps, { step_number: prev.steps.length + 1, name: '', description: '', agent_id: '', prompt_override: '' }]
    }));
  };

  const updateWorkflowStep = (idx, key, value) => {
    const updated = [...workflowForm.steps];
    updated[idx] = { ...updated[idx], [key]: value };
    setWorkflowForm(prev => ({ ...prev, steps: updated }));
  };

  const removeWorkflowStep = (idx) => {
    const updated = workflowForm.steps.filter((_, i) => i !== idx).map((s, i) => ({ ...s, step_number: i + 1 }));
    setWorkflowForm(prev => ({ ...prev, steps: updated }));
  };

  const moveWorkflowStep = (idx, direction) => {
    const newIdx = idx + direction;
    if (newIdx < 0 || newIdx >= workflowForm.steps.length) return;
    const updated = [...workflowForm.steps];
    [updated[idx], updated[newIdx]] = [updated[newIdx], updated[idx]];
    updated.forEach((s, i) => s.step_number = i + 1);
    setWorkflowForm(prev => ({ ...prev, steps: updated }));
  };

  const handleSaveWorkflow = async () => {
    if (!workflowForm.name.trim()) { toast.error('Workflow name required'); return; }
    try {
      if (editingWorkflow) {
        await updateWorkflow(editingWorkflow.id, workflowForm);
        toast.success('Workflow updated');
      } else {
        await createWorkflow(workflowForm);
        toast.success('Workflow created');
      }
      setShowWorkflowDialog(false);
      resetWorkflowForm();
      loadAll();
    } catch (err) { toast.error('Failed to save workflow'); }
  };

  const handleDeleteWorkflow = async (id) => {
    if (!window.confirm('Delete this workflow?')) return;
    try {
      await deleteWorkflow(id);
      setWorkflows(workflows.filter(w => w.id !== id));
      toast.success('Workflow deleted');
    } catch (err) { toast.error('Failed to delete'); }
  };

  // ---- TOOLS ----
  const openToolTest = (tool) => {
    setTestingTool(tool);
    // Initialize params from schema
    const params = {};
    if (tool.input_schema) {
      Object.keys(tool.input_schema).forEach(k => { params[k] = ''; });
    }
    setToolTestParams(params);
    setToolTestResult(null);
    setShowToolTestDialog(true);
  };

  const handleExecuteTool = async () => {
    if (!testingTool) return;
    setToolTestLoading(true);
    setToolTestResult(null);
    try {
      const res = await executeTool(testingTool.id, toolTestParams);
      setToolTestResult(res.data);
    } catch (err) {
      setToolTestResult({ error: err.response?.data?.detail || err.message });
    } finally {
      setToolTestLoading(false);
    }
  };

  // Get tool count attached to a skill
  const getSkillToolCount = (skill) => (skill.tools || []).length;

  // Get tools attached to a skill (resolved)
  const getSkillToolNames = (skill) => {
    return (skill.tools || []).map(tid => {
      const found = tools.find(t => t.id === tid);
      return found ? found.name : tid;
    });
  };

  // Get tools attached to agent via skills
  const getAgentToolCount = (agent) => {
    const agentSkillIds = agent.skills || [];
    const toolSet = new Set();
    agentSkillIds.forEach(sid => {
      const skill = skills.find(s => s.id === sid);
      if (skill && skill.tools) {
        skill.tools.forEach(tid => toolSet.add(tid));
      }
    });
    return toolSet.size;
  };

  const getIconComponent = (iconName) => {
    const IconComp = ICON_MAP[iconName] || Bot;
    return IconComp;
  };

  const getToolIcon = (iconName) => {
    const IconComp = TOOL_ICON_MAP[iconName] || Wrench;
    return IconComp;
  };

  return (
    <div className="page-container">
      {/* Hero Header */}
      <div className="page-hero" data-testid="agent-studio-hero">
        <div className="relative z-10 px-8 py-6 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-[hsl(var(--brand)/0.15)] border border-[hsl(var(--brand)/0.25)] flex items-center justify-center flex-shrink-0 animate-pulse-glow">
              <Bot className="w-6 h-6 text-[hsl(var(--brand))]" />
            </div>
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-white tracking-tight" style={{ fontFamily: 'Space Grotesk, sans-serif' }} data-testid="agent-studio-title">
                Agent Studio
              </h1>
              <p className="text-sm text-white/50 mt-0.5">Configure agents, skills, workflows, and tools for your pipeline</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {agents.length === 0 && (
              <Button onClick={handleSeedDefaults} className="bg-[hsl(var(--accent-warm))] text-[hsl(var(--accent-warm-foreground))] hover:bg-[hsl(var(--accent-warm)/0.88)] btn-press" data-testid="seed-defaults-btn">
                <Zap className="w-4 h-4 mr-2" /> Seed Default Agents
              </Button>
            )}
            <div className="text-center">
              <p className="text-[10px] uppercase tracking-[0.16em] text-white/40 mb-0.5">Agents</p>
              <p className="text-xl font-bold text-[hsl(var(--brand))] font-mono">{agents.length}</p>
            </div>
            <div className="w-px h-8 bg-white/10" />
            <div className="text-center">
              <p className="text-[10px] uppercase tracking-[0.16em] text-white/40 mb-0.5">Skills</p>
              <p className="text-xl font-bold text-[hsl(var(--status-success))] font-mono">{skills.length}</p>
            </div>
            <div className="w-px h-8 bg-white/10" />
            <div className="text-center">
              <p className="text-[10px] uppercase tracking-[0.16em] text-white/40 mb-0.5">Tools</p>
              <p className="text-xl font-bold text-[hsl(var(--status-warning))] font-mono">{tools.length}</p>
            </div>
          </div>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="mb-6">
          <TabsTrigger value="agents" data-testid="tab-agents"><Bot className="w-4 h-4 mr-2" /> Agents ({agents.length})</TabsTrigger>
          <TabsTrigger value="skills" data-testid="tab-skills"><Puzzle className="w-4 h-4 mr-2" /> Skills ({skills.length})</TabsTrigger>
          <TabsTrigger value="workflows" data-testid="tab-workflows"><Workflow className="w-4 h-4 mr-2" /> Workflows ({workflows.length})</TabsTrigger>
          <TabsTrigger value="tools" data-testid="tab-tools"><Wrench className="w-4 h-4 mr-2" /> Tools ({tools.length})</TabsTrigger>
        </TabsList>

        {/* ====== AGENTS TAB ====== */}
        <TabsContent value="agents">
          <div className="flex justify-end mb-4">
            <Button onClick={() => openAgentEditor()} className="bg-[hsl(var(--accent-warm))] text-[hsl(var(--accent-warm-foreground))] hover:bg-[hsl(var(--accent-warm)/0.88)] btn-press" data-testid="new-agent-btn">
              <Plus className="w-4 h-4 mr-2" /> New Agent
            </Button>
          </div>
          {agents.length === 0 ? (
            <Card className="bg-card/90 border border-border" style={{boxShadow: 'var(--shadow-elev-1)'}}>
              <CardContent className="text-center py-16">
                <Bot className="w-12 h-12 mx-auto mb-4 text-muted-foreground/30" />
                <p className="text-muted-foreground mb-4">No agents configured yet</p>
                <Button onClick={handleSeedDefaults} className="bg-[hsl(var(--accent))] text-white">
                  Seed Default Agents & Skills
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 stagger-grid">
              {agents.map(agent => {
                const IconComp = getIconComponent(agent.icon);
                const toolCount = getAgentToolCount(agent);
                return (
                  <Card key={agent.id} className="bg-[hsl(var(--pane))] border border-white/10 card-hover-lift" style={{boxShadow: 'var(--shadow-elev-1)'}} data-testid={`agent-card-${agent.id}`}>
                    <CardContent className="p-5">
                      <div className="flex items-start gap-4">
                        <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                          style={{ backgroundColor: `${agent.color}20`, color: agent.color }}>
                          <IconComp className="w-5 h-5" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <h3 className="font-semibold text-sm">{agent.name}</h3>
                            {agent.step_key && (
                              <Badge className="text-[10px] bg-[hsl(var(--accent)/0.15)] text-[hsl(var(--accent))] border border-[hsl(var(--accent)/0.25)]">
                                {agent.step_key}
                              </Badge>
                            )}
                          </div>
                          <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{agent.description}</p>
                          <div className="flex items-center gap-3 mt-2">
                            <Badge variant="outline" className="text-[10px] font-mono">{agent.model_provider}</Badge>
                            <span className="text-[10px] text-muted-foreground font-mono">{agent.model_name}</span>
                            <span className="text-[10px] text-muted-foreground">{agent.skills?.length || 0} skills</span>
                            {toolCount > 0 && (
                              <Badge variant="outline" className="text-[10px] bg-[hsl(var(--status-success)/0.1)] text-[hsl(var(--status-success))] border-[hsl(var(--status-success)/0.3)]">
                                <Wrench className="w-2.5 h-2.5 mr-0.5" />{toolCount} tools
                              </Badge>
                            )}
                            {agent.mcp_servers?.length > 0 && (
                              <Badge variant="outline" className="text-[10px]">
                                <Plug className="w-2.5 h-2.5 mr-0.5" />{agent.mcp_servers.length} MCP
                              </Badge>
                            )}
                          </div>
                        </div>
                      </div>
                      <Separator className="my-3" />
                      <div className="flex items-center justify-between">
                        <div className="flex gap-1 flex-wrap">
                          {agent.tags?.map(t => <Badge key={t} variant="outline" className="text-[10px]">{t}</Badge>)}
                        </div>
                        <div className="flex items-center gap-1">
                          {(() => {
                            const wfUsage = getAgentWorkflowUsage(agent);
                            if (wfUsage.length > 0) {
                              return (
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Badge 
                                        variant="outline" 
                                        className="text-[10px] cursor-help bg-[hsl(var(--accent)/0.08)] text-[hsl(var(--accent))] border-[hsl(var(--accent)/0.25)] hover:bg-[hsl(var(--accent)/0.15)] transition-colors"
                                        data-testid={`agent-workflow-usage-${agent.id}`}
                                      >
                                        <Workflow className="w-2.5 h-2.5 mr-1" />
                                        {wfUsage.length} workflow{wfUsage.length !== 1 ? 's' : ''}
                                      </Badge>
                                    </TooltipTrigger>
                                    <TooltipContent side="top" className="max-w-xs">
                                      <p className="font-medium text-xs mb-1">Used in:</p>
                                      {wfUsage.map((w, i) => (
                                        <p key={i} className="text-xs text-muted-foreground">
                                          &bull; {w.name} <span className="text-[10px] opacity-70">({w.stepName})</span>
                                        </p>
                                      ))}
                                    </TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              );
                            }
                            return (
                              <Badge variant="outline" className="text-[10px] text-muted-foreground/60 border-dashed" data-testid={`agent-workflow-usage-${agent.id}`}>
                                <Workflow className="w-2.5 h-2.5 mr-1 opacity-40" />
                                No workflows
                              </Badge>
                            );
                          })()}
                          <Button variant="ghost" size="sm" onClick={() => openAgentEditor(agent)} data-testid={`edit-agent-${agent.id}`}>
                            <Edit2 className="w-3.5 h-3.5" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleDeleteAgent(agent.id)} data-testid={`delete-agent-${agent.id}`}>
                            <Trash2 className="w-3.5 h-3.5 text-[hsl(var(--status-danger))]" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        {/* ====== SKILLS TAB ====== */}
        <TabsContent value="skills">
          <div className="flex justify-end mb-4">
            <Button onClick={() => openSkillEditor()} className="bg-[hsl(var(--accent-warm))] text-[hsl(var(--accent-warm-foreground))] hover:bg-[hsl(var(--accent-warm)/0.88)] btn-press" data-testid="new-skill-btn">
              <Plus className="w-4 h-4 mr-2" /> New Skill
            </Button>
          </div>
          {skills.length === 0 ? (
            <Card className="bg-card/90 border border-border" style={{boxShadow: 'var(--shadow-elev-1)'}}>
              <CardContent className="text-center py-16">
                <Puzzle className="w-12 h-12 mx-auto mb-4 text-muted-foreground/30" />
                <p className="text-muted-foreground">No skills yet. Create skills to enhance your agents.</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 stagger-grid">
              {skills.map(skill => (
                <Card key={skill.id} className="bg-[hsl(var(--pane))] border border-white/10 card-hover-lift" style={{boxShadow: 'var(--shadow-elev-1)'}} data-testid={`skill-card-${skill.id}`}>
                  <CardContent className="p-5">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <Puzzle className="w-4 h-4" style={{color: 'hsl(var(--accent))'}} />
                        <h3 className="font-semibold text-sm">{skill.name}</h3>
                      </div>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="sm" onClick={() => openSkillEditor(skill)}>
                          <Edit2 className="w-3.5 h-3.5" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDeleteSkill(skill.id)}>
                          <Trash2 className="w-3.5 h-3.5 text-[hsl(var(--status-danger))]" />
                        </Button>
                      </div>
                    </div>
                    <p className="text-xs text-muted-foreground mb-2">{skill.description}</p>
                    <p className="text-xs text-muted-foreground font-mono line-clamp-3 bg-secondary/30 p-2 rounded">{skill.content || 'No content'}</p>
                    <div className="flex flex-wrap gap-1 mt-2">
                      {skill.tags?.map(t => <Badge key={t} variant="outline" className="text-[10px]">{t}</Badge>)}
                    </div>
                    {/* Show attached tools */}
                    {getSkillToolCount(skill) > 0 && (
                      <div className="flex flex-wrap gap-1 mt-2 pt-2 border-t border-border/50">
                        {getSkillToolNames(skill).map((tName, i) => (
                          <Badge key={i} className="text-[10px] bg-[hsl(var(--status-success)/0.1)] text-[hsl(var(--status-success))] border border-[hsl(var(--status-success)/0.3)]">
                            <Wrench className="w-2.5 h-2.5 mr-0.5" /> {tName}
                          </Badge>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* ====== WORKFLOWS TAB ====== */}
        <TabsContent value="workflows">
          <div className="flex justify-end mb-4">
            <Button onClick={() => openWorkflowEditor()} className="bg-[hsl(var(--accent-warm))] text-[hsl(var(--accent-warm-foreground))] hover:bg-[hsl(var(--accent-warm)/0.88)] btn-press" data-testid="new-workflow-btn">
              <Plus className="w-4 h-4 mr-2" /> New Workflow
            </Button>
          </div>
          {workflows.length === 0 ? (
            <Card className="bg-card/90 border border-border" style={{boxShadow: 'var(--shadow-elev-1)'}}>
              <CardContent className="text-center py-16">
                <Workflow className="w-12 h-12 mx-auto mb-4 text-muted-foreground/30" />
                <p className="text-muted-foreground">No workflows yet. Create workflows to define agent pipelines.</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4 stagger-children">
              {workflows.map(wf => (
                <Card key={wf.id} className="bg-[hsl(var(--pane))] border border-white/10 card-hover-glow" style={{boxShadow: 'var(--shadow-elev-1)'}} data-testid={`workflow-card-${wf.id}`}>
                  <CardContent className="p-5">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <div className="flex items-center gap-2">
                          <Workflow className="w-4 h-4" style={{color: 'hsl(var(--accent))'}} />
                          <h3 className="font-semibold text-sm">{wf.name}</h3>
                          <Badge variant="outline" className="text-[10px]">{wf.steps?.length || 0} steps</Badge>
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">{wf.description}</p>
                      </div>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="sm" onClick={() => openWorkflowEditor(wf)}>
                          <Edit2 className="w-3.5 h-3.5" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDeleteWorkflow(wf.id)}>
                          <Trash2 className="w-3.5 h-3.5 text-[hsl(var(--status-danger))]" />
                        </Button>
                      </div>
                    </div>
                    {/* Steps visualization */}
                    <div className="flex items-center flex-wrap gap-1">
                      {(wf.steps || []).map((step, idx) => {
                        const assignedAgent = agents.find(a => a.id === step.agent_id);
                        return (
                          <React.Fragment key={idx}>
                            <div className="flex items-center gap-1 bg-secondary/40 rounded-lg px-3 py-1.5">
                              <span className="text-[10px] font-mono text-muted-foreground">{step.step_number}.</span>
                              <span className="text-xs font-medium">{step.name}</span>
                              {assignedAgent && (
                                <Badge className="text-[9px] ml-1" style={{backgroundColor: `${assignedAgent.color}20`, color: assignedAgent.color, border: `1px solid ${assignedAgent.color}40`}}>
                                  {assignedAgent.name}
                                </Badge>
                              )}
                            </div>
                            {idx < (wf.steps || []).length - 1 && <ChevronRight className="w-3.5 h-3.5 text-muted-foreground" />}
                          </React.Fragment>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* ====== TOOLS TAB ====== */}
        <TabsContent value="tools">
          <div className="mb-4">
            <p className="text-sm text-muted-foreground">
              Tools are executable scripts and APIs that agents can invoke during reasoning. Attach tools to Skills, then assign Skills to Agents.
            </p>
          </div>
          {tools.length === 0 ? (
            <Card className="bg-card/90 border border-border" style={{boxShadow: 'var(--shadow-elev-1)'}}>
              <CardContent className="text-center py-16">
                <Wrench className="w-12 h-12 mx-auto mb-4 text-muted-foreground/30" />
                <p className="text-muted-foreground">No tools available. Seed defaults to get starter tools.</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 stagger-grid">
              {tools.map(tool => {
                const ToolIcon = getToolIcon(tool.icon);
                const categoryColor = tool.category === 'security' ? 'hsl(var(--status-danger))' : 'hsl(var(--accent))';
                // Count how many skills use this tool
                const usedInSkills = skills.filter(s => (s.tools || []).includes(tool.id));
                return (
                  <Card key={tool.id} className="bg-[hsl(var(--pane))] border border-white/10 card-hover-lift" style={{boxShadow: 'var(--shadow-elev-1)'}} data-testid={`tool-card-${tool.id}`}>
                    <CardContent className="p-5">
                      <div className="flex items-start gap-3 mb-3">
                        <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                          style={{ backgroundColor: `${categoryColor}15`, color: categoryColor }}>
                          <ToolIcon className="w-5 h-5" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <h3 className="font-semibold text-sm">{tool.name}</h3>
                            {tool.builtin && (
                              <Badge className="text-[9px] bg-[hsl(var(--accent)/0.12)] text-[hsl(var(--accent))] border border-[hsl(var(--accent)/0.25)]">built-in</Badge>
                            )}
                          </div>
                          <p className="text-xs text-muted-foreground mt-1">{tool.description}</p>
                        </div>
                      </div>

                      {/* Input schema */}
                      <div className="bg-secondary/30 rounded-lg p-3 mb-3">
                        <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider mb-1.5">Parameters</p>
                        {tool.input_schema && Object.entries(tool.input_schema).map(([key, type]) => (
                          <div key={key} className="flex items-center gap-2 text-xs">
                            <code className="text-[hsl(var(--accent))] font-mono">{key}</code>
                            <span className="text-muted-foreground">{type}</span>
                          </div>
                        ))}
                      </div>

                      {/* Usage info */}
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="text-[10px]">{tool.category}</Badge>
                          {usedInSkills.length > 0 && (
                            <span className="text-[10px] text-muted-foreground">
                              Used in {usedInSkills.length} skill{usedInSkills.length > 1 ? 's' : ''}
                            </span>
                          )}
                        </div>
                        <Button variant="outline" size="sm" className="text-xs h-7" onClick={() => openToolTest(tool)} data-testid={`test-tool-${tool.id}`}>
                          <Play className="w-3 h-3 mr-1" /> Test
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* ====== AGENT DIALOG ====== */}
      <Dialog open={showAgentDialog} onOpenChange={setShowAgentDialog}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingAgent ? 'Edit Agent' : 'Create Agent'}</DialogTitle>
            <DialogDescription>Configure agent name, prompts, model, and skills</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Name *</Label>
                <Input value={agentForm.name} onChange={(e) => setAgentForm({...agentForm, name: e.target.value})} placeholder="Research Agent" data-testid="agent-name-input" />
              </div>
              <div>
                <Label>Color</Label>
                <div className="flex gap-2">
                  <input type="color" value={agentForm.color} onChange={(e) => setAgentForm({...agentForm, color: e.target.value})} className="w-10 h-9 rounded border border-border cursor-pointer" />
                  <Input value={agentForm.color} onChange={(e) => setAgentForm({...agentForm, color: e.target.value})} className="flex-1 bg-secondary/40" />
                </div>
              </div>
            </div>
            <div>
              <Label>Description</Label>
              <Input value={agentForm.description} onChange={(e) => setAgentForm({...agentForm, description: e.target.value})} placeholder="Brief description of what this agent does" />
            </div>
            <div>
              <Label>System Prompt *</Label>
              <Textarea value={agentForm.system_prompt} onChange={(e) => setAgentForm({...agentForm, system_prompt: e.target.value})}
                placeholder="You are an expert..." className="min-h-[200px] font-mono text-sm bg-secondary/40" data-testid="agent-prompt-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Model Provider</Label>
                <Select value={agentForm.model_provider} onValueChange={(v) => {
                  const provider = llmProviders.find(p => p.id === v);
                  const defaultModel = provider?.models?.find(m => m.default)?.id || provider?.models?.[0]?.id || '';
                  setAgentForm({...agentForm, model_provider: v, model_name: defaultModel});
                }}>
                  <SelectTrigger className="bg-secondary/40"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {llmProviders.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Model</Label>
                <Select value={agentForm.model_name} onValueChange={(v) => setAgentForm({...agentForm, model_name: v})}>
                  <SelectTrigger className="bg-secondary/40 font-mono text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {llmProviders.find(p => p.id === agentForm.model_provider)?.models?.map(m => (
                      <SelectItem key={m.id} value={m.id} className="font-mono text-xs">{m.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            {/* Fallback Model */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-muted-foreground">Fallback Provider</Label>
                <Select value={agentForm.fallback_model_provider || 'none'} onValueChange={(v) => setAgentForm({...agentForm, fallback_model_provider: v === 'none' ? '' : v})}>
                  <SelectTrigger className="bg-secondary/40 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {llmProviders.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-muted-foreground">Fallback Model</Label>
                <Input value={agentForm.fallback_model_name || ''} onChange={(e) => setAgentForm({...agentForm, fallback_model_name: e.target.value})} placeholder="e.g. gpt-4o" className="bg-secondary/40 font-mono text-xs" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Temperature ({agentForm.temperature})</Label>
                <input type="range" min="0" max="1" step="0.1" value={agentForm.temperature}
                  onChange={(e) => setAgentForm({...agentForm, temperature: parseFloat(e.target.value)})}
                  className="w-full" />
              </div>
              <div>
                <Label>Max Tokens</Label>
                <Input type="number" value={agentForm.max_tokens} onChange={(e) => setAgentForm({...agentForm, max_tokens: parseInt(e.target.value)})} className="bg-secondary/40" />
              </div>
            </div>
            {/* Skills selection */}
            <div>
              <Label>Attached Skills</Label>
              <div className="flex flex-wrap gap-2 mt-1">
                {skills.map(s => (
                  <Badge key={s.id}
                    className={`cursor-pointer text-xs transition-colors ${agentForm.skills.includes(s.id) ? 'bg-[hsl(var(--accent)/0.2)] text-[hsl(var(--accent))] border border-[hsl(var(--accent)/0.4)]' : 'bg-secondary text-muted-foreground hover:bg-secondary/80'}`}
                    onClick={() => {
                      const newSkills = agentForm.skills.includes(s.id)
                        ? agentForm.skills.filter(id => id !== s.id)
                        : [...agentForm.skills, s.id];
                      setAgentForm({...agentForm, skills: newSkills});
                    }}
                  >
                    <Puzzle className="w-3 h-3 mr-1" /> {s.name}
                    {(s.tools || []).length > 0 && (
                      <span className="ml-1 text-[9px] opacity-70">({(s.tools || []).length} tools)</span>
                    )}
                  </Badge>
                ))}
                {skills.length === 0 && <span className="text-xs text-muted-foreground">No skills available. Create skills first.</span>}
              </div>
            </div>
            {/* Tags */}
            <div>
              <Label>Tags (comma-separated)</Label>
              <Input value={agentForm.tags.join(', ')} onChange={(e) => setAgentForm({...agentForm, tags: e.target.value.split(',').map(t => t.trim()).filter(Boolean)})} placeholder="research, security" className="bg-secondary/40" />
            </div>
            {/* MCP Configuration */}
            <Separator />
            <div>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Server className="w-4 h-4 text-[hsl(var(--accent))]" />
                  <Label className="text-sm">MCP Servers</Label>
                </div>
                <Button variant="outline" size="sm" className="text-xs h-7" onClick={() => {
                  setAgentForm({...agentForm, mcp_servers: [...agentForm.mcp_servers, { name: '', type: 'custom', transport: 'http', endpoint_url: '', auth_token: '', tool_whitelist: [], enabled: true }]});
                }}>
                  <Plus className="w-3 h-3 mr-1" /> Add Server
                </Button>
              </div>
              {agentForm.mcp_servers.length === 0 ? (
                <p className="text-xs text-muted-foreground text-center py-3 bg-secondary/20 rounded-lg">No MCP servers configured</p>
              ) : (
                <div className="space-y-3">
                  {agentForm.mcp_servers.map((mcp, i) => (
                    <Card key={i} className="border bg-secondary/20 p-3 space-y-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Plug className="w-3.5 h-3.5 text-muted-foreground" />
                          <span className="text-xs font-medium">{mcp.name || `MCP Server ${i + 1}`}</span>
                          <Badge variant="outline" className="text-[10px]">{mcp.type}</Badge>
                        </div>
                        <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => {
                          const updated = [...agentForm.mcp_servers];
                          updated.splice(i, 1);
                          setAgentForm({...agentForm, mcp_servers: updated});
                        }}>
                          <Trash2 className="w-3 h-3 text-destructive" />
                        </Button>
                      </div>
                      <div className="grid grid-cols-3 gap-2">
                        <Input value={mcp.name} placeholder="Server name" className="text-xs h-7 bg-secondary/40" onChange={(e) => {
                          const u = [...agentForm.mcp_servers]; u[i] = {...u[i], name: e.target.value}; setAgentForm({...agentForm, mcp_servers: u});
                        }} />
                        <Select value={mcp.type} onValueChange={(v) => {
                          const u = [...agentForm.mcp_servers]; u[i] = {...u[i], type: v}; setAgentForm({...agentForm, mcp_servers: u});
                        }}>
                          <SelectTrigger className="text-xs h-7 bg-secondary/40"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="canva">Canva MCP</SelectItem>
                            <SelectItem value="figma">Figma MCP</SelectItem>
                            <SelectItem value="custom">Custom MCP</SelectItem>
                          </SelectContent>
                        </Select>
                        <Select value={mcp.transport} onValueChange={(v) => {
                          const u = [...agentForm.mcp_servers]; u[i] = {...u[i], transport: v}; setAgentForm({...agentForm, mcp_servers: u});
                        }}>
                          <SelectTrigger className="text-xs h-7 bg-secondary/40"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="http">HTTP</SelectItem>
                            <SelectItem value="sse">SSE</SelectItem>
                            <SelectItem value="websocket">WebSocket</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <Input value={mcp.endpoint_url} placeholder="https://mcp-server.example.com" className="text-xs h-7 bg-secondary/40" onChange={(e) => {
                        const u = [...agentForm.mcp_servers]; u[i] = {...u[i], endpoint_url: e.target.value}; setAgentForm({...agentForm, mcp_servers: u});
                      }} />
                      <Input value={mcp.auth_token} placeholder="Auth token (optional)" type="password" className="text-xs h-7 bg-secondary/40" onChange={(e) => {
                        const u = [...agentForm.mcp_servers]; u[i] = {...u[i], auth_token: e.target.value}; setAgentForm({...agentForm, mcp_servers: u});
                      }} />
                    </Card>
                  ))}
                </div>
              )}
            </div>
            <Button onClick={handleSaveAgent} className="w-full bg-[hsl(var(--accent))] text-white" data-testid="save-agent-btn">
              <Save className="w-4 h-4 mr-2" /> {editingAgent ? 'Update Agent' : 'Create Agent'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ====== SKILL DIALOG ====== */}
      <Dialog open={showSkillDialog} onOpenChange={setShowSkillDialog}>
        <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingSkill ? 'Edit Skill' : 'Create Skill'}</DialogTitle>
            <DialogDescription>Skills are reusable knowledge modules that agents can use. Attach tools to give agents executable capabilities.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Name *</Label>
              <Input value={skillForm.name} onChange={(e) => setSkillForm({...skillForm, name: e.target.value})} placeholder="Cybersecurity Research" data-testid="skill-name-input" />
            </div>
            <div>
              <Label>Description</Label>
              <Input value={skillForm.description} onChange={(e) => setSkillForm({...skillForm, description: e.target.value})} placeholder="Brief description" />
            </div>
            <div>
              <Label>Content (Markdown)</Label>
              <Textarea value={skillForm.content} onChange={(e) => setSkillForm({...skillForm, content: e.target.value})}
                placeholder="Expert knowledge, instructions, context..." className="min-h-[200px] font-mono text-sm bg-secondary/40" data-testid="skill-content-input" />
            </div>
            {/* Tool Attachment */}
            <Separator />
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Wrench className="w-4 h-4 text-[hsl(var(--accent))]" />
                <Label className="text-sm">Attached Tools</Label>
              </div>
              <p className="text-xs text-muted-foreground mb-2">
                Select tools that agents with this skill can invoke automatically during reasoning.
              </p>
              <div className="flex flex-wrap gap-2" data-testid="skill-tools-selector">
                {tools.map(tool => {
                  const isSelected = skillForm.tools.includes(tool.id);
                  const ToolIcon = getToolIcon(tool.icon);
                  return (
                    <Badge key={tool.id}
                      className={`cursor-pointer text-xs transition-colors ${isSelected
                        ? 'bg-[hsl(var(--status-success)/0.2)] text-[hsl(var(--status-success))] border border-[hsl(var(--status-success)/0.4)]'
                        : 'bg-secondary text-muted-foreground hover:bg-secondary/80'
                      }`}
                      onClick={() => {
                        const newTools = isSelected
                          ? skillForm.tools.filter(id => id !== tool.id)
                          : [...skillForm.tools, tool.id];
                        setSkillForm({...skillForm, tools: newTools});
                      }}
                      data-testid={`skill-tool-toggle-${tool.id}`}
                    >
                      <ToolIcon className="w-3 h-3 mr-1" /> {tool.name}
                      {isSelected && <CheckCircle2 className="w-3 h-3 ml-1" />}
                    </Badge>
                  );
                })}
                {tools.length === 0 && (
                  <span className="text-xs text-muted-foreground">No tools available. Seed defaults to add tools.</span>
                )}
              </div>
              {skillForm.tools.length > 0 && (
                <p className="text-xs text-muted-foreground mt-2">
                  {skillForm.tools.length} tool{skillForm.tools.length > 1 ? 's' : ''} attached. Agents with this skill will be able to auto-invoke these tools.
                </p>
              )}
            </div>
            <Separator />
            <div>
              <Label>Tags (comma-separated)</Label>
              <Input value={skillForm.tags.join(', ')} onChange={(e) => setSkillForm({...skillForm, tags: e.target.value.split(',').map(t => t.trim()).filter(Boolean)})} placeholder="security, research" className="bg-secondary/40" />
            </div>
            <Button onClick={handleSaveSkill} className="w-full bg-[hsl(var(--accent))] text-white" data-testid="save-skill-btn">
              <Save className="w-4 h-4 mr-2" /> {editingSkill ? 'Update Skill' : 'Create Skill'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ====== WORKFLOW DIALOG ====== */}
      <Dialog open={showWorkflowDialog} onOpenChange={setShowWorkflowDialog}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingWorkflow ? 'Edit Workflow' : 'Create Workflow'}</DialogTitle>
            <DialogDescription>Define the steps and assign agents to each step</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Workflow Name *</Label>
              <Input value={workflowForm.name} onChange={(e) => setWorkflowForm({...workflowForm, name: e.target.value})} placeholder="Email Campaign Pipeline" data-testid="workflow-name-input" />
            </div>
            <div>
              <Label>Description</Label>
              <Input value={workflowForm.description} onChange={(e) => setWorkflowForm({...workflowForm, description: e.target.value})} placeholder="Brief description" />
            </div>
            <Separator />
            <div className="flex items-center justify-between">
              <Label>Steps</Label>
              <Button variant="outline" size="sm" onClick={addWorkflowStep} data-testid="add-workflow-step-btn">
                <Plus className="w-3.5 h-3.5 mr-1" /> Add Step
              </Button>
            </div>
            {workflowForm.steps.map((step, idx) => (
              <Card key={idx} className="bg-secondary/20 border border-border">
                <CardContent className="p-3">
                  <div className="flex items-center justify-between mb-2">
                    <Badge className="bg-secondary text-foreground text-xs">Step {step.step_number}</Badge>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="sm" onClick={() => moveWorkflowStep(idx, -1)} disabled={idx === 0}><MoveUp className="w-3.5 h-3.5" /></Button>
                      <Button variant="ghost" size="sm" onClick={() => moveWorkflowStep(idx, 1)} disabled={idx === workflowForm.steps.length - 1}><MoveDown className="w-3.5 h-3.5" /></Button>
                      <Button variant="ghost" size="sm" onClick={() => removeWorkflowStep(idx)}><Trash2 className="w-3.5 h-3.5 text-[hsl(var(--status-danger))]" /></Button>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-xs">Step Name</Label>
                      <Input value={step.name} onChange={(e) => updateWorkflowStep(idx, 'name', e.target.value)} placeholder="Research" className="bg-secondary/40" />
                    </div>
                    <div>
                      <Label className="text-xs">Assign Agent</Label>
                      <Select value={step.agent_id || ''} onValueChange={(v) => updateWorkflowStep(idx, 'agent_id', v)}>
                        <SelectTrigger className="bg-secondary/40"><SelectValue placeholder="Select agent" /></SelectTrigger>
                        <SelectContent>
                          {agents.map(a => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="mt-2">
                    <Label className="text-xs">Description</Label>
                    <Input value={step.description} onChange={(e) => updateWorkflowStep(idx, 'description', e.target.value)} placeholder="What this step does" className="bg-secondary/40" />
                  </div>
                </CardContent>
              </Card>
            ))}
            {workflowForm.steps.length === 0 && (
              <p className="text-sm text-muted-foreground text-center py-4">Add steps to define the workflow</p>
            )}
            <div>
              <Label>Tags (comma-separated)</Label>
              <Input value={workflowForm.tags.join(', ')} onChange={(e) => setWorkflowForm({...workflowForm, tags: e.target.value.split(',').map(t => t.trim()).filter(Boolean)})} className="bg-secondary/40" />
            </div>
            <Button onClick={handleSaveWorkflow} className="w-full bg-[hsl(var(--accent))] text-white" data-testid="save-workflow-btn">
              <Save className="w-4 h-4 mr-2" /> {editingWorkflow ? 'Update Workflow' : 'Create Workflow'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ====== TOOL TEST DIALOG ====== */}
      <Dialog open={showToolTestDialog} onOpenChange={setShowToolTestDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Play className="w-4 h-4 text-[hsl(var(--accent))]" />
              Test Tool: {testingTool?.name}
            </DialogTitle>
            <DialogDescription>{testingTool?.description}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {/* Parameter inputs */}
            {testingTool?.input_schema && Object.entries(testingTool.input_schema).map(([key, type]) => (
              <div key={key}>
                <Label className="text-sm">{key} <span className="text-muted-foreground text-xs">({type})</span></Label>
                <Input
                  value={toolTestParams[key] || ''}
                  onChange={(e) => setToolTestParams({...toolTestParams, [key]: e.target.value})}
                  placeholder={`Enter ${key}...`}
                  className="bg-secondary/40 font-mono text-sm"
                  data-testid={`tool-test-param-${key}`}
                />
              </div>
            ))}

            <Button
              onClick={handleExecuteTool}
              disabled={toolTestLoading}
              className="w-full bg-[hsl(var(--accent))] text-white"
              data-testid="execute-tool-btn"
            >
              {toolTestLoading ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Executing...</>
              ) : (
                <><Play className="w-4 h-4 mr-2" /> Execute Tool</>
              )}
            </Button>

            {/* Result display */}
            {toolTestResult && (
              <div className="mt-2" data-testid="tool-test-result">
                <Label className="text-xs mb-1 block">Result</Label>
                <ScrollArea className="h-[200px]">
                  <pre className="text-xs font-mono bg-secondary/40 p-3 rounded-lg overflow-x-auto whitespace-pre-wrap">
                    {JSON.stringify(toolTestResult, null, 2)}
                  </pre>
                </ScrollArea>
                {toolTestResult?.result?.error ? (
                  <div className="flex items-center gap-1 mt-2 text-xs text-[hsl(var(--status-danger))]">
                    <XCircle className="w-3.5 h-3.5" /> Error in execution
                  </div>
                ) : (
                  <div className="flex items-center gap-1 mt-2 text-xs text-[hsl(var(--status-success))]">
                    <CheckCircle2 className="w-3.5 h-3.5" /> Tool executed successfully
                  </div>
                )}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
